import React, { useState } from 'react';
import { PackageBadge } from './PackageStatus';
import { toast } from 'sonner';
import { Globe, Star, Download, Flame, Loader2, ScanSearch, Trash2, ChevronLeft, ChevronRight, MapPin, ExternalLink, Mail, Instagram, Facebook, Linkedin } from 'lucide-react';

const STATUS_STYLES = {
  new: 'bg-gray-400/10 text-gray-400 border-gray-400/20',
  contacted: 'bg-sky-400/10 text-sky-400 border-sky-400/20',
  proposal_sent: 'bg-violet-400/10 text-violet-400 border-violet-400/20',
  meeting_booked: 'bg-amber-400/10 text-amber-400 border-amber-400/20',
  won: 'bg-emerald-400/10 text-emerald-400 border-emerald-400/20',
  lost: 'bg-red-400/10 text-red-400 border-red-400/20',
};

const FavStar = ({ lead, fav, onToggle }) => (
  <button onClick={() => onToggle(lead, !fav)} title={fav ? 'Remove from favorites' : 'Add to favorites'}
    aria-pressed={fav} data-testid={`lead-fav-${lead.lead_id}`}
    className={`transition-transform hover:scale-110 ${fav ? 'text-amber-400' : 'text-slate-400 hover:text-amber-300'}`}>
    <Star size={14} className={fav ? 'fill-amber-400' : ''} />
  </button>
);

export const LeadsTable = ({ theme, data, filters, setFilters, loading, workspaceId, api, onOpenLead, onRefresh }) => {
  const { isDarkMode, cardBg, cardBorder, textPrimary, textSecondary, inputBg } = theme;
  const [selected, setSelected] = useState([]);
  const [favs, setFavs] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const leads = data.leads || [];

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v, page: 1 }));
  const toggleSel = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const allSelected = leads.length > 0 && leads.every(l => selected.includes(l.lead_id));

  const exportCsv = async () => {
    try {
      const p = new URLSearchParams({ status: filters.status, city: filters.city, industry: filters.industry, q: filters.q, flag: filters.flag, favorite: filters.favorite, pkg: filters.pkg });
      const r = await api.get(`/api/workspaces/${workspaceId}/leads/export/csv?${p}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = 'leads.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export failed'); }
  };

  const byId = Object.fromEntries(leads.map(l => [l.lead_id, l]));
  const busyPkg = l => ['queued', 'running', 'ready'].includes(l?.package_status);
  const eligible = selected.filter(id => byId[id]?.website && !busyPkg(byId[id]));
  const noSite = selected.filter(id => byId[id] && !byId[id].website).length;
  const bulkAnalyze = () => {
    if (!eligible.length) { toast.error(noSite ? 'Deep analysis needs a website — none of the selected businesses have one.' : 'These businesses are already queued, analyzing or done.'); return; }
    window.dispatchEvent(new CustomEvent('voxinity:lead-package-request', { detail: { lead_ids: eligible, skipped: noSite } }));
  };

  const deleteOne = async (l) => {
    if (!window.confirm(`Delete "${l.name}"? This also removes its proposals.`)) return;
    try {
      await api.delete(`/api/workspaces/${workspaceId}/leads/${l.lead_id}`);
      toast.success('Lead deleted');
      setSelected(s => s.filter(x => x !== l.lead_id));
      onRefresh();
    } catch { toast.error('Delete failed'); }
  };

  const bulkDelete = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Delete ${selected.length} lead${selected.length > 1 ? 's' : ''}? This also removes their proposals.`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post(`/api/workspaces/${workspaceId}/leads/bulk-delete`, { lead_ids: selected });
      toast.success(`Deleted ${r.data.deleted} lead${r.data.deleted > 1 ? 's' : ''}`);
      setSelected([]); onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
    finally { setBulkBusy(false); }
  };

  const toggleFav = async (l, next) => {
    setFavs(f => ({ ...f, [l.lead_id]: next }));
    try {
      await api.put(`/api/workspaces/${workspaceId}/leads/${l.lead_id}`, { favorite: next });
      if (filters.favorite) onRefresh();
    } catch { setFavs(f => ({ ...f, [l.lead_id]: !next })); toast.error('Could not update favorite'); }
  };

  const selCls = `px-2.5 py-2 rounded-lg ${inputBg} border ${cardBorder} ${textPrimary} text-xs focus:outline-none`;
  const totalPages = Math.max(1, Math.ceil((data.total || 0) / 50));

  return (
    <div className={`rounded-2xl border ${cardBorder} ${cardBg} overflow-hidden`} data-testid="leads-table">
      {/* Filter bar */}
      <div className={`flex flex-wrap items-center gap-2 px-4 py-3 border-b ${cardBorder}`}>
        <input value={filters.q} onChange={e => setF('q', e.target.value)} placeholder="Search name / owner / website…"
          className={`${selCls} w-52`} data-testid="leads-filter-search" />
        <select value={filters.status} onChange={e => setF('status', e.target.value)} className={selCls} data-testid="leads-filter-status">
          <option value="">All statuses</option>
          {Object.keys(STATUS_STYLES).map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        <select value={filters.city} onChange={e => setF('city', e.target.value)} className={selCls} data-testid="leads-filter-city">
          <option value="">All cities</option>
          {(data.cities || []).map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filters.industry} onChange={e => setF('industry', e.target.value)} className={selCls} data-testid="leads-filter-industry">
          <option value="">All industries</option>
          {(data.industries || []).map(i => <option key={i} value={i}>{i}</option>)}
        </select>
        <select value={filters.pkg} onChange={e => setF('pkg', e.target.value)} className={selCls} data-testid="leads-filter-pkg">
          <option value="">Deep Analysis: all</option>
          <option value="none">Not analyzed</option>
          <option value="queued">Queued</option>
          <option value="running">Analyzing</option>
          <option value="ready">Proposal ready</option>
          <option value="failed">Failed</option>
        </select>
        <button onClick={() => setF('favorite', filters.favorite ? '' : '1')} data-testid="leads-filter-favorites" aria-pressed={!!filters.favorite}
          className={`${selCls} flex items-center gap-1.5 transition-colors ${filters.favorite ? '!border-amber-400/50 !text-amber-300 bg-amber-400/10' : ''}`}>
          <Star size={12} className={filters.favorite ? 'fill-amber-400 text-amber-400' : ''} /> Favorites
        </button>
        <select value={filters.sort} onChange={e => setF('sort', e.target.value)} className={selCls} data-testid="leads-sort">
          <option value="created_at">Newest first</option>
          <option value="leak_score">Leak score</option>
          <option value="leak_usd">$ leaking</option>
          <option value="reviews">Most reviews</option>
          <option value="name">Name A-Z</option>
        </select>
        <div className="flex-1" />
        {selected.length > 0 && (
          <>
            <button onClick={bulkAnalyze} disabled={bulkBusy}
              className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-violet-500 text-white font-medium hover:bg-violet-400"
              data-testid="leads-bulk-analyze-btn">
              {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <ScanSearch size={12} />}
              Run Deep Analysis &amp; Proposal ({eligible.length})
            </button>
            <button onClick={bulkDelete} disabled={bulkBusy}
              className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border font-medium ${isDarkMode ? 'border-red-400/40 text-red-300 hover:bg-red-400/10' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
              data-testid="leads-bulk-delete-btn">
              <Trash2 size={12} /> Delete {selected.length}
            </button>
          </>
        )}
        <button onClick={exportCsv}
          className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border ${cardBorder} ${textPrimary} ${inputBg}`}
          data-testid="leads-export-btn">
          <Download size={12} /> Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className={`text-[11px] uppercase tracking-wide ${textSecondary} border-b ${cardBorder}`}>
              <th className="pl-4 pr-1 py-2.5 text-left w-14">
                <input type="checkbox" checked={allSelected}
                  onChange={() => setSelected(allSelected ? [] : leads.map(l => l.lead_id))}
                  data-testid="leads-select-all" />
              </th>
              <th className="px-2 py-2.5 text-left">Business</th>
              <th className="px-2 py-2.5 text-left">Phone</th>
              <th className="px-2 py-2.5 text-left">Links</th>
              <th className="px-2 py-2.5 text-left">Reviews</th>
              <th className="px-2 py-2.5 text-left">Leak</th>
              <th className="px-2 py-2.5 text-left">Status</th>
              <th className="px-2 py-2.5 text-left">Deep Analysis</th>
              <th className="px-2 py-2.5 w-10" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className={`px-4 py-10 text-center ${textSecondary}`}>Loading…</td></tr>
            )}
            {!loading && leads.length === 0 && (
              <tr><td colSpan={9} className={`px-4 py-12 text-center ${textSecondary}`} data-testid="leads-empty">
                No leads yet — run a search to find businesses.
              </td></tr>
            )}
            {leads.map(l => {
              const a = l.analysis || {};
              return (
                <tr key={l.lead_id}
                  className={`border-b ${cardBorder} cursor-pointer ${isDarkMode ? 'hover:bg-white/[0.03]' : 'hover:bg-gray-50'}`}
                  onClick={() => onOpenLead(l)} data-testid={`lead-row-${l.lead_id}`}>
                  <td className="pl-4 pr-1 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" checked={selected.includes(l.lead_id)} onChange={() => toggleSel(l.lead_id)}
                        data-testid={`lead-check-${l.lead_id}`} />
                      <FavStar lead={l} fav={favs[l.lead_id] ?? !!l.favorite} onToggle={toggleFav} />
                    </div>
                  </td>
                  <td className="px-2 py-3">
                    <p className={`font-medium ${textPrimary} truncate max-w-[220px] flex items-center gap-1.5`}>
                      {l.name}
                      {l.building && (
                        <span title="Building AI agents…" data-testid={`lead-building-${l.lead_id}`}>
                          <Loader2 size={11} className="animate-spin text-teal-400 shrink-0" />
                        </span>
                      )}
                    </p>
                    <p className={`text-[11px] ${textSecondary} mt-0.5`}>
                      {l.industry}{l.city ? ` · ${l.city}` : ''}
                    </p>
                  </td>
                  <td className={`px-2 py-3 text-xs ${textSecondary} whitespace-nowrap`}>{l.phone || '—'}</td>
                  <td className="px-2 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      {l.website ? (
                        <a href={l.website} target="_blank" rel="noreferrer" title={l.website}
                          className="flex items-center gap-1 text-[11px] text-teal-400 hover:underline"
                          data-testid={`lead-site-${l.lead_id}`}>
                          <Globe size={11} /> Site <ExternalLink size={8} />
                        </a>
                      ) : (
                        <span className={`text-[11px] ${textSecondary} flex items-center gap-1 opacity-50`}><Globe size={11} /> —</span>
                      )}
                      {l.google_maps_uri && (
                        <a href={l.google_maps_uri} target="_blank" rel="noreferrer" title="Google Maps"
                          className={`flex items-center gap-1 text-[11px] ${textSecondary} hover:text-teal-400`}
                          data-testid={`lead-maps-${l.lead_id}`}>
                          <MapPin size={11} /> Maps
                        </a>
                      )}
                      {l.email && (
                        <a href={`mailto:${l.email}`} title={l.email}
                          className={`${textSecondary} hover:text-teal-400`}
                          data-testid={`lead-email-${l.lead_id}`}><Mail size={11} /></a>
                      )}
                      {l.socials?.instagram && (
                        <a href={l.socials.instagram} target="_blank" rel="noreferrer" title="Instagram"
                          className="text-pink-400 hover:opacity-70" data-testid={`lead-ig-${l.lead_id}`}><Instagram size={11} /></a>
                      )}
                      {l.socials?.facebook && (
                        <a href={l.socials.facebook} target="_blank" rel="noreferrer" title="Facebook"
                          className="text-blue-400 hover:opacity-70" data-testid={`lead-fb-${l.lead_id}`}><Facebook size={11} /></a>
                      )}
                      {l.socials?.linkedin && (
                        <a href={l.socials.linkedin} target="_blank" rel="noreferrer" title="LinkedIn"
                          className="text-sky-400 hover:opacity-70" data-testid={`lead-li-${l.lead_id}`}><Linkedin size={11} /></a>
                      )}
                    </div>
                  </td>
                  <td className={`px-2 py-3 text-xs ${textSecondary} whitespace-nowrap`}>
                    {l.rating ? (
                      <span className="flex items-center gap-1">
                        <Star size={11} className="text-amber-400" /> {l.rating} ({l.review_count})
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-2 py-3">
                    {a.status === 'running' && (
                      <span className="flex items-center gap-1 text-[11px] text-sky-400 whitespace-nowrap">
                        <Loader2 size={11} className="animate-spin" /> analyzing…
                      </span>
                    )}
                    {a.status === 'done' && (
                      <span className="flex items-center gap-1.5 whitespace-nowrap">
                        <Flame size={12} className={a.leak_score >= 70 ? 'text-red-400' : a.leak_score >= 40 ? 'text-amber-400' : 'text-gray-400'} />
                        <span className={`text-xs font-semibold ${textPrimary}`}>{a.leak_score}</span>
                        <span className="text-[11px] text-red-400 font-medium">
                          ${Number(a.total_monthly_leak_usd || 0).toLocaleString()}/mo
                        </span>
                      </span>
                    )}
                    {(!a.status || a.status === 'none') && <span className={`text-[11px] ${textSecondary}`}>—</span>}
                    {a.status === 'error' && <span className="text-[11px] text-red-400">failed</span>}
                  </td>
                  <td className="px-2 py-3">
                    <span className={`text-[10px] px-2 py-1 rounded-full border font-medium whitespace-nowrap ${STATUS_STYLES[l.status] || STATUS_STYLES.new}`}>
                      {(l.status || 'new').replace('_', ' ')}
                    </span>
                  </td>
                  <td className={`px-2 py-3 text-xs ${textSecondary}`} data-testid={`lead-pkg-cell-${l.lead_id}`}>
                    {l.package_status ? <PackageBadge lead={l} /> : '—'}
                  </td>
                  <td className="px-2 py-3" onClick={e => e.stopPropagation()}>
                    <button onClick={() => deleteOne(l)} title="Delete lead"
                      className={`p-1.5 rounded-lg ${textSecondary} hover:text-red-400 ${isDarkMode ? 'hover:bg-red-400/10' : 'hover:bg-red-50'}`}
                      data-testid={`lead-delete-${l.lead_id}`}>
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className={`flex items-center justify-between px-4 py-2.5 border-t ${cardBorder}`}>
        <span className={`text-[11px] ${textSecondary}`} data-testid="leads-total">{data.total || 0} leads</span>
        <div className="flex items-center gap-2">
          <button disabled={filters.page <= 1} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}
            className={`p-1.5 rounded-lg border ${cardBorder} ${textSecondary} disabled:opacity-40`} data-testid="leads-prev-page">
            <ChevronLeft size={13} />
          </button>
          <span className={`text-[11px] ${textSecondary}`}>{filters.page} / {totalPages}</span>
          <button disabled={filters.page >= totalPages} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}
            className={`p-1.5 rounded-lg border ${cardBorder} ${textSecondary} disabled:opacity-40`} data-testid="leads-next-page">
            <ChevronRight size={13} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeadsTable;
